import api from "../../services/api";
import config from "../../config";
import logger from "../../utils/logger";
import { Eventbus } from "../../utils";
import events from "../../utils/constants/events";
import { normalizeDraft, normalizeSubordinates } from "./helpers";

export default function createScorecardService({ employeeEmailOverride, managerEmailOverride } = {}) {
  const configuredEmployeeEmail = employeeEmailOverride ?? config.employeeEmail ?? null;
  const configuredManagerEmail = managerEmailOverride ?? config.managerEmail ?? null;
  let currentScorecard = null;
  let history = [];
  let goalsCache = {};
  let scorecardCache = {};
  let isLoading = false;

  function getData() {
    return currentScorecard;
  }

  function getHistory() {
    return [...history];
  }

  function getLoadingState() {
    return isLoading;
  }

  function getCachedGoals(kpiDataId) {
    return goalsCache[kpiDataId] ?? null;
  }

  function getCachedScorecard(kpiDataId) {
    return scorecardCache[kpiDataId] ?? null;
  }

  async function loadHistory(employeeEmail) {
    try {
      const response = await api.getScorecardHistory(employeeEmail || configuredEmployeeEmail);

      if (response?.code === 'ERR_CANCELED') {
        throw new Error('Request was cancelled');
      }

      history = Array.isArray(response) ? response : [];
    } catch (error) {
      logger.error("Error loading scorecard history:", error);
      throw error;
    }
    return history;
  }

  async function loadGoals(kpiDataId) {
    if (!kpiDataId) {
      return [];
    }

    const cached = goalsCache[kpiDataId];
    if (cached) {
      return cached;
    }

    try {
      const response = await api.getKpiGoals(kpiDataId);
      const goals = Array.isArray(response) ? response : [];
      goalsCache[kpiDataId] = goals;
      return goals;
    } catch (error) {
      logger.error("Error loading KPI goals:", error);
      throw error;
    }
  }

  async function loadScorecardById(kpiDataId) {
    if (!kpiDataId) {
      return null;
    }

    const cached = scorecardCache[kpiDataId];
    if (cached) {
      return cached;
    }

    try {
      const response = await api.getScorecardById(kpiDataId);
      const normalized = normalizeDraft(response);
      if (normalized) {
        scorecardCache[kpiDataId] = normalized;
      }
      return normalized;
    } catch (error) {
      logger.error("Error loading scorecard by ID:", error);
      throw error;
    }
  }

  async function loadCurrentScorecard(employeeEmail) {
    isLoading = true;
    Eventbus.emit(events.SCORECARD_DATA_UPDATED);

    try {
      const response = await api.getCurrentScorecard(employeeEmail || configuredEmployeeEmail);
      let goals = response?.goals ?? [];

      if (response?.kpiDataId) {
        try {
          goals = await loadGoals(response.kpiDataId);
        } catch {
          // Goals failed to load but scorecard is still usable
        }
      }

      currentScorecard = normalizeDraft(response, goals);

      if (currentScorecard?.kpiDataId) {
        scorecardCache[currentScorecard.kpiDataId] = currentScorecard;
      }

      Eventbus.emit(events.SCORECARD_DATA_LOADED, currentScorecard);
      return currentScorecard;
    } catch (error) {
      logger.error("Error loading current scorecard:", error);
      throw error;
    } finally {
      isLoading = false;
      Eventbus.emit(events.SCORECARD_DATA_UPDATED);
    }
  }

  async function refreshCurrentScorecard(employeeEmail) {
    isLoading = true;
    Eventbus.emit(events.SCORECARD_DATA_UPDATED);

    const email = employeeEmail || configuredEmployeeEmail;

    try {
      await api.refreshKpiData(email);

      // KPI data changed server-side — drop caches so we pull fresh values
      goalsCache = {};
      scorecardCache = {};

      const response = await api.getCurrentScorecard(email);
      let goals = response?.goals ?? [];

      if (response?.kpiDataId) {
        try {
          goals = await loadGoals(response.kpiDataId);
        } catch {
          // Goals failed to load but scorecard is still usable
        }
      }

      currentScorecard = normalizeDraft(response, goals);

      if (currentScorecard?.kpiDataId) {
        scorecardCache[currentScorecard.kpiDataId] = currentScorecard;
      }

      Eventbus.emit(events.SCORECARD_DATA_LOADED, currentScorecard);
      return currentScorecard;
    } catch (error) {
      logger.error("Error refreshing KPI data:", error);
      throw error;
    } finally {
      isLoading = false;
      Eventbus.emit(events.SCORECARD_DATA_UPDATED);
    }
  }

  async function triggerDataRefresh() {
    try {
      return await api.triggerDataRefresh();
    } catch (error) {
      logger.error("Error triggering data refresh:", error);
      throw error;
    }
  }

  async function loadHistoricalScorecard(kpiDataId) {
    if (!kpiDataId) {
      return null;
    }

    isLoading = true;
    Eventbus.emit(events.SCORECARD_DATA_UPDATED);

    try {
      const scorecard = await loadScorecardById(kpiDataId);
      let goals = scorecard?.goals ?? [];

      if (scorecard?.kpiDataId) {
        try {
          goals = await loadGoals(scorecard.kpiDataId);
        } catch {
          // Goals failed but scorecard is still usable
        }
      }

      const normalized = normalizeDraft(scorecard, goals);

      if (normalized?.kpiDataId) {
        scorecardCache[normalized.kpiDataId] = normalized;
      }

      return normalized;
    } catch (error) {
      logger.error("Error loading historical scorecard:", error);
      throw error;
    } finally {
      isLoading = false;
      Eventbus.emit(events.SCORECARD_DATA_UPDATED);
    }
  }

  async function loadPreviousMonthData(previousMonthKPIDataId) {
    if (!previousMonthKPIDataId) {
      return { goals: [], scorecard: null };
    }

    const [goals, scorecard] = await Promise.all([
      loadGoals(previousMonthKPIDataId).catch(() => []),
      loadScorecardById(previousMonthKPIDataId).catch(() => null),
    ]);

    return { goals, scorecard };
  }

  async function saveScorecard(draft) {
    try {
      const response = await api.upsertScorecard(draft);
      const saved = normalizeDraft(response ?? draft, draft?.goals);

      if (saved?.kpiDataId) {
        scorecardCache[saved.kpiDataId] = saved;
      }

      currentScorecard = saved;
      Eventbus.emit(events.SCORECARD_DATA_LOADED, currentScorecard);
      return saved;
    } catch (error) {
      logger.error("Error saving scorecard:", error);
      throw error;
    }
  }

  async function saveGoal(payload) {
    try {
      const savedGoal = await api.upsertKpiGoal(payload);
      const result = savedGoal ?? payload;

      if (payload.kpiDataId && goalsCache[payload.kpiDataId]) {
        const existing = goalsCache[payload.kpiDataId];
        const matchIndex = existing.findIndex((g) => (
          g.id === result.id
          || (g.kpiFocusFieldName === result.kpiFocusFieldName && g.kpiFocusGroupName === result.kpiFocusGroupName)
        ));
        if (matchIndex === -1) {
          goalsCache[payload.kpiDataId] = [...existing, result];
        } else {
          goalsCache[payload.kpiDataId] = existing.map((g, i) => (i === matchIndex ? result : g));
        }
      }

      return result;
    } catch (error) {
      logger.error("Error saving KPI goal:", error);
      throw error;
    }
  }

  async function deleteGoal(id, kpiDataId) {
    try {
      await api.deleteKpiGoal(id);

      if (kpiDataId && goalsCache[kpiDataId]) {
        goalsCache[kpiDataId] = goalsCache[kpiDataId].filter((g) => g.id !== id);
      }
    } catch (error) {
      logger.error("Error deleting KPI goal:", error);
      throw error;
    }
  }

  async function saveReflection(payload) {
    try {
      const saved = await api.upsertKpiGoalReflection(payload);
      return saved ?? payload;
    } catch (error) {
      logger.error("Error saving reflection:", error);
      throw error;
    }
  }

  function invalidateGoalsCache(kpiDataId) {
    if (kpiDataId) {
      delete goalsCache[kpiDataId];
    }
  }

  async function loadSubordinates() {
    try {
      const response = await api.getSubordinates(configuredManagerEmail);
      return normalizeSubordinates(response);
    } catch (error) {
      logger.error("Error loading subordinates:", error);
      throw error;
    }
  }

  function getManagerEmail() {
    return configuredManagerEmail;
  }

  function getEmployeeEmail() {
    return configuredEmployeeEmail;
  }

  async function submitFeedback(payload) {
    try {
      const response = await api.submitFeedback(payload);
      return response?.data ?? payload;
    } catch (error) {
      logger.error("Error submitting feedback:", error);
      throw error;
    }
  }

  async function deleteFeedback(id) {
    try {
      await api.deleteFeedback(id);
      return true;
    } catch (error) {
      logger.error("Error deleting feedback:", error);
      throw error;
    }
  }

  function clear() {
    currentScorecard = null;
    history = [];
    goalsCache = {};
    scorecardCache = {};
    isLoading = false;
  }

  return {
    getData,
    getHistory,
    getLoadingState,
    getCachedGoals,
    getCachedScorecard,
    loadHistory,
    loadGoals,
    loadScorecardById,
    loadCurrentScorecard,
    refreshCurrentScorecard,
    triggerDataRefresh,
    loadHistoricalScorecard,
    loadPreviousMonthData,
    loadSubordinates,
    getManagerEmail,
    getEmployeeEmail,
    saveScorecard,
    saveGoal,
    deleteGoal,
    saveReflection,
    submitFeedback,
    deleteFeedback,
    invalidateGoalsCache,
    clear,
  };
}
